import * as React from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { Check } from "lucide-react";
import { Input as InputPrimitive } from "@base-ui/react/input";
import { cn } from "cn";
import { EASE_OUT } from "lib/ease";

import "./input.css";

const inputBaseClassName = "h-8 w-full min-w-0 bg-transparent px-2.5 py-1 text-base transition-colors outline-none file:inline-flex file:h-6 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-input/50 disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 md:text-sm dark:bg-input/30 dark:disabled:bg-input/80 dark:aria-invalid:border-destructive/50";

const hasClassNames = (classNames) => classNames && Object.values(classNames).some(Boolean);

const Input = React.forwardRef(function Input(
  {
    className,
    classNames,
    error,
    label,
    leftIcon,
    onBlur,
    onChange,
    onFocus,
    onValueChange,
    reserveErrorLine = false,
    rightIcon,
    success = false,
    type,
    variant = "default",
    ...props
  },
  ref,
) {
  const reduce = useReducedMotion();
  const [focused, setFocused] = React.useState(false);
  const generatedId = React.useId();
  const inputId = props.id || generatedId;
  const errorId = `${inputId}-error`;
  const enhanced = Boolean(
    label ||
      leftIcon ||
      rightIcon ||
      error ||
      reserveErrorLine ||
      success ||
      onValueChange ||
      hasClassNames(classNames)
  );

  const handleChange = (event) => {
    onChange?.(event);
    onValueChange?.(event.target.value);
  };

  const handleFocus = (event) => {
    setFocused(true);
    onFocus?.(event);
  };

  const handleBlur = (event) => {
    setFocused(false);
    onBlur?.(event);
  };

  const inputClassName = cn(inputBaseClassName, classNames?.input, className);

  if (!enhanced) {
    return (
      <InputPrimitive
        ref={ref}
        type={type}
        data-slot="input"
        data-variant={variant}
        className={inputClassName}
        onChange={onChange}
        {...props}
      />
    );
  }

  return (
    <div className={cn("ui-input-field", classNames?.root)}>
      {label ? (
        <motion.label
          className={cn("ui-input-label", classNames?.label)}
          htmlFor={inputId}
          animate={{ color: focused ? "#142936" : "#61737d" }}
          transition={{ duration: reduce ? 0 : 0.16, ease: EASE_OUT }}
        >
          {label}
        </motion.label>
      ) : null}

      <motion.div
        className={cn("ui-input-shell", classNames?.shell)}
        animate={
          error
            ? { x: reduce ? 0 : [0, -4, 4, -3, 3, 0] }
            : {
                borderColor: focused ? "#33cdd1" : "#d2d9db",
                x: 0,
              }
        }
        transition={
          error
            ? { duration: reduce ? 0 : 0.36, ease: EASE_OUT }
            : { duration: reduce ? 0 : 0.18, ease: EASE_OUT }
        }
      >
        {leftIcon ? <span className={cn("ui-input-icon", classNames?.leftIcon)}>{leftIcon}</span> : null}
        <InputPrimitive
          ref={ref}
          id={inputId}
          type={type}
          data-slot="input"
          data-variant={variant}
          aria-describedby={error ? errorId : props["aria-describedby"]}
          aria-invalid={error ? true : props["aria-invalid"]}
          className={cn("ui-input-control", inputClassName)}
          onBlur={handleBlur}
          onChange={handleChange}
          onFocus={handleFocus}
          {...props}
        />
        {rightIcon ? <span className={cn("ui-input-icon", classNames?.rightIcon)}>{rightIcon}</span> : null}
        {success && !rightIcon ? (
          <motion.span
            aria-hidden="true"
            className={cn("ui-input-success", classNames?.success)}
            initial={reduce ? false : { opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: reduce ? 0 : 0.18, ease: EASE_OUT }}
          >
            <Check size={15} />
          </motion.span>
        ) : null}
      </motion.div>

      <AnimatePresence initial={false} mode="wait">
        {typeof error === "string" ? (
          <motion.div
            key="error"
            aria-live="polite"
            className={cn("ui-input-error", classNames?.error)}
            id={errorId}
            initial={reduce ? false : { height: 0, opacity: 0, y: -4 }}
            animate={{ height: "auto", opacity: 1, y: 0 }}
            exit={reduce ? undefined : { height: 0, opacity: 0, y: -4 }}
            transition={{ duration: reduce ? 0 : 0.18, ease: EASE_OUT }}
          >
            {error}
          </motion.div>
        ) : reserveErrorLine ? (
          <div aria-hidden="true" className="ui-input-error ui-input-error--reserved" key="reserved" />
        ) : null}
      </AnimatePresence>
    </div>
  );
});

export { Input };
